import { cva } from 'class-variance-authority'

/**
 * Banner Slider CVA Configuration
 *
 * Design tokens used:
 * - main-color (#5D5C98) - Primary brand color
 * - custom-red (#f9394c) - Accent color
 * - cards-bg (#F5F5F5) - Background color
 * - border-color (#f0f0f0) - Border color
 */

/**
 * Banner section container
 */
export const bannerSectionVariants = cva('homepage-banner-slider mb-8')

/**
 * Banner item container (each slide)
 */
export const bannerItemVariants = cva(
  'relative overflow-hidden rounded bg-cards-bg'
)

/**
 * Banner link wrapper
 * Includes focus-visible for accessibility and visited state consistency
 */
export const bannerLinkVariants = cva(
  'block focus:outline-none focus-visible:ring-2 focus-visible:ring-main-color focus-visible:ring-offset-2 ' +
    'visited:text-inherit visited:no-underline'
)

/**
 * Banner image styling
 * Fixed aspect ratio + object-cover keeps every banner the same size
 * regardless of upload dimensions (narrower images get center-cropped, no
 * stretching). Recommended uploads: 1920x600 desktop, 750x600 mobile.
 */
export const bannerImageVariants = cva('w-full object-cover', {
  variants: {
    visibility: {
      desktop: 'hidden md:block aspect-[1920/600]',
      mobile: 'block md:hidden aspect-[750/600]',
    },
  },
  defaultVariants: {
    visibility: 'desktop',
  },
})

/**
 * Banner fallback (when no images)
 * Matches loaded-banner aspect ratio so layout stays stable across states.
 */
export const bannerFallbackVariants = cva(
  'aspect-[750/600] md:aspect-[1920/600] bg-gradient-to-r from-main-color to-custom-red flex items-center justify-center'
)

/**
 * Banner fallback content container
 */
export const bannerFallbackContentVariants = cva('text-center text-white p-8')

/**
 * Banner fallback title
 */
export const bannerFallbackTitleVariants = cva('text-2xl md:text-4xl font-bold mb-4 font-main-font')

/**
 * Banner fallback CTA button
 */
export const bannerFallbackCtaVariants = cva(
  'inline-block px-6 py-2 bg-white text-main-color rounded-full font-medium font-sec-font ' +
    'hover:bg-cards-bg transition-colors'
)

/**
 * Navigation button (prev/next arrows)
 *
 * Variants:
 * - size: 'banner' (larger, default) or 'product' (smaller)
 * - direction: 'prev' or 'next' (controls left/right position)
 */
export const bannerNavButtonVariants = cva(
  'absolute top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white ' +
    'rounded-full flex items-center justify-center transition-all duration-200 ' +
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ' +
    'disabled:opacity-50 disabled:cursor-not-allowed',
  {
    variants: {
      direction: {
        prev: '',
        next: '',
      },
      size: {
        // Banner slider: larger buttons, shadow-xl
        banner: 'w-14 h-14 md:w-16 md:h-16 shadow-xl focus-visible:ring-main-color',
        // Product slider: smaller buttons with border, shadow-md
        product: 'w-10 h-10 md:w-12 md:h-12 shadow-md hover:shadow-lg border border-gray-200 ' +
          'backdrop-blur-sm focus-visible:ring-primary-500',
      },
    },
    compoundVariants: [
      // Banner positioning
      { size: 'banner', direction: 'prev', class: 'left-1 md:left-4' },
      { size: 'banner', direction: 'next', class: 'right-1 md:right-4' },
      // Product positioning (slightly more inset)
      { size: 'product', direction: 'prev', class: 'left-2 md:left-4' },
      { size: 'product', direction: 'next', class: 'right-2 md:right-4' },
    ],
    defaultVariants: {
      direction: 'prev',
      size: 'banner',
    },
  }
)

/**
 * Navigation button icon
 * Size: w-7 h-7 = 28px on mobile, w-8 h-8 = 32px on desktop
 */
export const bannerNavIconVariants = cva('w-8 h-8 md:w-10 md:h-10 text-text-color')

/**
 * Swiper container styles
 */
export const bannerSwiperVariants = cva('relative')

/**
 * Banner skeleton (loading state)
 * Matches loaded-banner aspect ratio so layout stays stable across states.
 */
export const bannerSkeletonVariants = cva(
  'aspect-[750/600] md:aspect-[1920/600] rounded animate-pulse bg-muted'
)

/**
 * Swiper pagination CSS classes
 * These are used in Swiper config, not as CVA variants
 */
export const SWIPER_PAGINATION_BULLET =
  'swiper-pagination-bullet !w-3 !h-3 !bg-custom-gray !opacity-100'
export const SWIPER_PAGINATION_BULLET_ACTIVE = '!bg-main-color'
